import React, { useEffect } from 'react';
import { Button } from '../ui/button';
import { Trash2, Loader2, Eye, ArrowLeft, Play } from 'lucide-react';

/**
 * SessionList — read-only HISTORY panel of archived conversations ("limpiar chat"
 * moves the active chat here). The user can VIEW a past conversation or REMOVE it
 * from their view (soft-delete; the server retains it for legal purposes). There
 * is no create/rename here — the active chat lives in the main view.
 */
const SessionList = ({
  history,
  loadingSessions,
  loadHistory,
  viewingArchivedId,
  viewArchived,
  exitArchived,
  confirmDeleteSession,
  onResume,
}) => {
  // Fetch the archived list whenever the panel mounts (it is only rendered open).
  useEffect(() => {
    if (loadHistory) loadHistory();
  }, [loadHistory]);

  return (
    <div className="border-b border-white/20 bg-slate-50 dark:bg-slate-900/60 max-h-64 overflow-y-auto">
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-sm">Historial de conversaciones</h3>
          {viewingArchivedId && (
            <Button
              onClick={exitArchived}
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
            >
              <ArrowLeft className="w-3 h-3 mr-1" />
              Volver al chat
            </Button>
          )}
        </div>

        {loadingSessions ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
          </div>
        ) : !history || history.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
            Sin conversaciones archivadas. Usa «Limpiar» para guardar la actual aquí.
          </p>
        ) : (
          <div className="space-y-2">
            {history.map((session) => (
              <div
                key={session.id}
                onClick={() => viewArchived(session.id)}
                className={`p-3 rounded-lg transition-all group cursor-pointer ${
                  viewingArchivedId === session.id
                    ? 'bg-sky-100 dark:bg-sky-900/50 border-2 border-sky-500'
                    : 'bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{session.title}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {new Date(session.updated_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); viewArchived(session.id); }}
                      className="h-6 w-6 p-0 hover:bg-sky-100 hover:text-sky-600 dark:hover:bg-sky-900/50"
                      title="Ver (solo lectura)"
                    >
                      <Eye className="w-3 h-3" />
                    </Button>
                    {onResume && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onResume(session.id); }}
                        className="h-6 w-6 p-0 hover:bg-green-100 hover:text-green-600 dark:hover:bg-green-900/50 dark:hover:text-green-300"
                        title="Continuar conversación"
                      >
                        <Play className="w-3 h-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => confirmDeleteSession(session.id, e)}
                      className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/50 dark:hover:text-red-300"
                      title="Eliminar de mi vista"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionList;
